import { mulBp, withinRange } from '../math/fixed.ts';
import type { BattleState, Enemy, FieldPt, PassCub, SendaNpc } from '../types.ts';
import { damageHero, ev, findEnemy, spawnEnemy } from './combat.ts';
import { stepToward } from './enemies.ts';

/**
 * The Ventisquero's cubs (docs/sendas/guerrero.md §5: «bolas de nieve que caminan, revientan y empujan»), shared by the
 * four acts of chapter 5. A cub is a goblin with a cub's life, drawn as a ball of snow (the renderer reads the act's
 * list of cubs); the act moves it (it never fights): it walks to what it goes for and bursts there. In its way, a hero
 * makes it burst early: a blow of `heroBp` of its life and a push of `push`. Killing it first is the point.
 *
 * Cost: a handful alive at a time; each looks at the hero once a tick.
 */

type Cub = { hp: number; heroBp: number; push: number; blows: number };

/** A cub where it comes from, going for `to` (the act's meaning). */
export const cubSpawn = (s: BattleState, list: PassCub[], at: FieldPt, to: number, hp: number): PassCub => {
  const e = spawnEnemy(s, 'goblin', 1, false, 0, at.x, at.y, hp);
  e.damage = 0;
  const c: PassCub = { id: e.id, to, t: 0 };
  list.push(c);
  return c;
};

/** The cubs still standing (the dead and burst ones out of the list). */
export const cubsAlive = (s: BattleState, list: PassCub[]): PassCub[] => {
  let n = 0;
  for (const c of list) {
    const e = findEnemy(s, c.id);
    if (e && e.hp > 0) list[n++] = c;
  }
  list.length = n;
  return list;
};

/** It bursts: gone, a puff. */
export const cubBurst = (s: BattleState, e: Enemy): void => {
  e.hp = 0;
  ev(s, { t: 'senda', kind: 'burst', npc: '', x: e.x, y: e.y, n: e.id });
};

/** A cub's step toward a point; a standing hero in its way makes it burst on the hero (true: it burst). */
export const cubStep = (s: BattleState, e: Enemy, x: number, y: number, rules: Cub, px: number): boolean => {
  for (const h of s.heroes) {
    if (h.downTicks > 0 || !withinRange(h.x, h.y, e.x, e.y, 450)) continue;
    damageHero(s, h, mulBp(h.stats.maxHp, rules.heroBp), null, 'physical', true);
    // Pushed back along the cub's way.
    const dx = h.x - e.x;
    const dy = h.y - e.y;
    const d = Math.abs(dx) + Math.abs(dy) || 1;
    h.x += Math.trunc((dx * rules.push) / d);
    h.y += Math.trunc((dy * rules.push) / d);
    h.destX = h.ax = h.x;
    h.destY = h.ay = h.y;
    h.path = [];
    h.moving = false;
    cubBurst(s, e);
    return true;
  }
  e.anim = 'walk';
  stepToward(e, x, y, px);
  return false;
};

/** A cub bursting on one of the path's people: its blows. */
export const cubOnPerson = (s: BattleState, e: Enemy, n: SendaNpc, blows: number): void => {
  n.hp = n.hp > blows ? n.hp - blows : 0;
  ev(s, { t: 'senda', kind: n.hp > 0 ? 'hit' : 'down', npc: n.id, x: n.x, y: n.y });
  cubBurst(s, e);
};
